import React, { useRef, useState } from "react";
import ModalBasic from "./ModalBasic.jsx";
import styled from "styled-components";
import useStore from "../../store.js";
import { createGlobalStyle } from "styled-components";
import ModalLawCharge from "./ModalLawCharge.jsx";
import ModalUnauthorizedUser from "./ModalUnauthorizedUser.jsx";
import { useNavigate } from "react-router-dom";

// ModalBasic 컴포넌트를 세분화한 삭제 재확인 컴포넌트

/* 
buttonType prop
- "queryButton" : 질문 버튼(10로우 차감)
- "comparisonButton" : 비교 버튼(5로우 차감)
 */
const ModalPayCheck = ({
  isOpen,
  onClose,
  onConfirm,
  law,
  buttonType,
  headerContent,
  additionalContent,
  checkCreditBeforeConfirm = true,
}) => {
  const [showModal, setShowModal] = useState(false); // 로우 부족 알림 Modal창
  const [showUnauthorizedModal, setShowUnauthorizedModal] = useState(false); // Unauthorized 모달창 상태 변수
  const confirming = useRef(false);
  const [isConfirming, setIsConfirming] = useState(false);

  const navigate = useNavigate();
  const { userInfo, updateUserInfo } = useStore();

  const actionLabel = buttonType === "comparisonButton"
    ? "비교하기"
    : buttonType === "queryButton" ? "검색하기" : headerContent || "진행하기";
  const description = additionalContent || (buttonType === "comparisonButton"
    ? "이 판례와 내 상황의 공통점·차이점을 확인해요."
    : buttonType === "queryButton" ? "입력한 상황을 바탕으로 비슷한 판례를 찾아요." : null);
  const credit = userInfo?.creditAmount;
  const hasBalance = typeof credit === "number" && Number.isFinite(credit);

  const confirmOnce = async () => {
    if (confirming.current) return;
    confirming.current = true;
    setIsConfirming(true);
    try {
      await handleConfirm();
    } finally {
      confirming.current = false;
      setIsConfirming(false);
    }
  };

  // 결제 버튼을 눌렀을 때 로우 개수 확인
  const handleConfirm = async () => {
    // 실제 차감과 잔액 판정을 서버가 원자적으로 처리하는 기능은
    // 오래된 브라우저 잔액으로 요청을 미리 차단하지 않는다.
    if (!checkCreditBeforeConfirm) {
      await onConfirm();
      return;
    }

    if (!userInfo) {
      // 알림 후 재시도
      window.alert(
        "사용자 정보가 없어 결제를 할 수 없습니다. 재로그인 후 다시 시도해주십시오."
      );
      return;
    }

    const requiredLaw =
      typeof law === "number"
        ? law
        : buttonType === "queryButton"
          ? 10
          : buttonType === "comparisonButton"
            ? 5
            : 5;

    if (userInfo.creditAmount >= requiredLaw) {
      // '질문하기' 팝업의 '확인' 버튼이 눌리고 로우 개수가 10개 이상일 때
      // '비교하기' 팝업의 '확인' 버튼이 눌리고 로우 개수가 2개 이상일 때
      // '대화 초기화' 팝업의 '확인' 버튼이 눌리고 로우 개수가 5개 이상일 때
      // 서버에 결제 진행 및 데이터 요청
      await onConfirm();

      // 결제 진행 후 로우 개수(creditAmount) 업데이트를 위해
      try {
        await updateUserInfo(setShowUnauthorizedModal); // store의 updateUserInfo 함수를 호출하여 userInfo를 업데이트
      } catch (error) {
        window.alert("결제 과정에서 문제가 발생했습니다. 다시 시도해 주세요.");
        console.error("Error during payment and user info update:", error);
      }
    } else {
      // 로우 개수가 부족할 때
      onClose(); // 이 Modal창은 닫고
      setShowModal(true); // 로우 부족 알림 Modal창 Open
    }
  };

  const handleUnauthorizedClose = () => {
    setShowUnauthorizedModal(false);
    navigate("/");
  };

  // 위치 top: 50%, left: 50%
  return (
    <>
      <GlobalStyle />
      <ModalBasic
        isOpen={isOpen}
        onClose={onClose}
        width={window.innerWidth > 430 ? "400px" : "312px"}
        height={window.innerWidth > 430 ? "auto" : "auto"}
        top="50%"
        left="50%"
        className="modal-pay-check-content">
        <Wrapper>
          <TextWrapper>
            <Header>{headerContent || actionLabel}</Header>
            <Content>
              {law}로우로 {actionLabel}
            </Content>
            {description && (
              <AdditionalContent>{description}</AdditionalContent>
            )}
            {hasBalance && (
              <Balance>
                보유 {credit.toLocaleString("ko-KR")}로우
                {typeof law === "number" && credit >= law && ` · 사용 후 ${(credit - law).toLocaleString("ko-KR")}로우`}
              </Balance>
            )}
          </TextWrapper>
          <ButtonWrapper $buttonType={buttonType}>
            <ButtonStyles onClick={onClose} type="button" $variant="cancel" disabled={isConfirming}>
              취소
            </ButtonStyles>
            <ButtonStyles
              onClick={confirmOnce}
              type="button"
              $variant="confirm"
              disabled={isConfirming}
              style={{ color: "#00F370" }}>
              {isConfirming ? "진행 중…" : `${law}로우로 ${actionLabel}`}
            </ButtonStyles>
          </ButtonWrapper>
        </Wrapper>
      </ModalBasic>
      <ModalLawCharge isOpen={showModal} onClose={() => setShowModal(false)} />
      {showUnauthorizedModal && (
        <ModalUnauthorizedUser
          isOpen={showUnauthorizedModal}
          onClose={handleUnauthorizedClose}
        />
      )}
    </>
  );
};

/* Mobile - top:48% left:50% */
const GlobalStyle = createGlobalStyle`
  @media (max-width: 430px) {
    .modal-pay-check-content {
      top: 48% !important;
      left: 50% !important;
    }
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px 32px;

  @media (max-width: 430px) {
    padding: 24px;
  }
`;

const Header = styled.div`
  color: rgba(0, 0, 0, 0.6);
  text-align: center;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
  display: inline;
`;

const Content = styled.div`
  color: #000;
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  line-height: 32px;
  margin-top: 8px;
  word-break: keep-all;

  @media (max-width: 430px) {
    font-size: 16px;
    line-height: 24px;
  }
`;

const AdditionalContent = styled.div`
  color: rgba(0, 0, 0, 0.6);
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px;
  margin-top: 8px;

  @media (max-width: 430px) {
    font-size: 12px;
    line-height: 20px;
  }
`;

const TextWrapper = styled.div``;

const Balance = styled.p`
  margin: 12px 0 0;
  color: #52605a;
  font-size: 12px;
  font-weight: 500;
  line-height: 20px;
  word-break: keep-all;
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  margin-top: 12px;

  /* 모바일 환경에서는 항상 표시 */
  @media (max-width: 430px) {
    margin-top: ${(props) =>
      props.$buttonType === "queryButton" ? "16px" : "12px"};
  } // 검색하기 버튼일 때 16px, 비교하기 버튼일 때 12px
`;

const ButtonStyles = styled.button`
  min-width: 70px;
  min-height: 44px;
  padding: 0 12px;
  margin-left: 8px;
  border: none;
  background-color: #f0f0f0;
  cursor: pointer;
  border-radius: 6px;
  background: ${(props) => (props.$variant === "cancel" ? "#F2F2F2" : "#000")};
  color: ${(props) => (props.$variant === "cancel" ? "#000" : "#FFF")};
  font: inherit;
  font-size: 13px;
  white-space: nowrap;
  &:disabled { opacity: 0.6; cursor: wait; }
  &:focus-visible { outline: 2px solid #00873e; outline-offset: 3px; }
  /* 버튼 텍스트 수직 수평 정중앙 */
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
`;

export default ModalPayCheck;
